import React, { useEffect, useState } from "react";
import { Button, Col, message, Modal, Row, Table, Tag, TimePicker } from "antd";
import _ from "underscore";
import {
    cancelSign,
    exportSignList,
    getSignList,
    repairSign,
    SignItem,
    syncSignList
} from "service/service";
import { Moment } from "moment";
import style from "./index.module.scss";
import { RiShareBoxFill } from "react-icons/ri";
import { AiOutlineSync } from "react-icons/ai";

interface ILiveSignLogProps {
    courseNo: string;
    courseSeq: string;
    section: string;
    week: string;
    weekTime: string;
    date: Moment | null;
    channelId: string;
}

export default function LiveSignLog({
    courseNo,
    courseSeq,
    section,
    week,
    weekTime,
    date,
    channelId
}: ILiveSignLogProps) {
    const [data, setData] = useState<SignItem[]>([]);
    const [signModal, setSignModal] = useState(false);
    const [signUser, setSignUser] = useState("");
    const [signTime, setSignTime] = useState<Moment | undefined>(undefined);

    useEffect(() => {
        getSignList(courseNo, courseSeq, section, week, weekTime).then(res =>
            setData(_.sortBy(res, item => item.signTime.length))
        );
    }, [courseNo, courseSeq, section, week, weekTime]);

    function handleDeleteSign(id: string) {
        cancelSign(id)
            .then(res => {
                setData(
                    _.map(data, item => {
                        item.signTime = _.reject(
                            item.signTime,
                            time => time.ids === id
                        );
                        if (item.signTime.length === 0) {
                            item.signDate = "";
                        }
                        return item;
                    })
                );
                message.success("取消签到成功！");
            })
            .catch(res => message.error(res.message));
    }

    function handleSign() {
        if (!signTime) {
            message.error("签到时间不能为空！");
            return;
        }
        repairSign(
            courseNo,
            courseSeq,
            section,
            week,
            weekTime,
            signUser,
            `${date && date.format("YYYY-MM-DD")} ${signTime.format(
                "HH:mm:ss"
            )}`
        )
            .then(res => {
                message.success("签到成功！");
                setData(
                    _.map(data, item => {
                        if (item.studentNo === signUser) {
                            item.signTime.push({
                                ids: res,
                                time: signTime.format("HH:mm")
                            });
                            item.signDate = date!.format("YYYY-MM-DD");
                        }
                        return item;
                    })
                );
                setSignModal(false);
                setSignUser("");
                setSignTime(undefined);
            })
            .catch(err => message.error(err.response.data.message));
    }

    function renderAction(id: string) {
        return (
            <Button
                type="link"
                className={style.link}
                onClick={() => {
                    setSignModal(true);
                    setSignUser(id);
                }}>
                补签
            </Button>
        );
    }

    function renderTime(time: { ids: string; time: string }[]) {
        return time.map(item => (
            <Tag
                closable
                onClose={(e: any) => onDeleteTime(e, item.ids)}
                key={item.ids}>
                {item.time}
            </Tag>
        ));
    }

    function onDeleteTime(e: any, id: string) {
        e.preventDefault();
        Modal.confirm({
            title: "提示",
            content: `确认要删除学员${id}的签到记录吗?`,
            onOk: () => handleDeleteSign(id)
        });
    }

    return (
        <>
            <div className={style.buttons}>
                <Button
                    onClick={() =>
                        syncSignList(channelId, date!.format("YYYY-MM-DD"))
                    }>
                    <AiOutlineSync size={20} /> 手动同步签到数据
                </Button>
                <Button
                    onClick={() =>
                        exportSignList(
                            courseNo,
                            courseSeq,
                            weekTime,
                            week,
                            section
                        )
                    }>
                    <RiShareBoxFill size={20} /> 导出签到列表
                </Button>
            </div>

            <Table
                dataSource={data}
                bordered
                pagination={false}
                rowKey="studentNo"
                className={style.table}>
                <Table.Column
                    title="序号"
                    dataIndex="index"
                    key="index"
                    render={(text, record, index) => index + 1}
                    width={100}
                    align="center"
                />
                <Table.Column
                    title="学号"
                    dataIndex="studentNo"
                    key="studentNo"
                    width={200}
                    align="center"
                />
                <Table.Column
                    title="姓名"
                    dataIndex="studentName"
                    key="studentName"
                />
                <Table.Column
                    title="签到日期"
                    dataIndex="signDate"
                    key="signDate"
                    width={150}
                    align="center"
                />
                <Table.Column
                    title="签到时间"
                    dataIndex="signTime"
                    key="signTime"
                    render={renderTime}
                    width={300}
                    align="left"
                />
                <Table.Column
                    title="操作"
                    dataIndex="studentNo"
                    key="action"
                    render={renderAction}
                    align="center"
                    width={200}
                />
            </Table>

            <Modal
                title="补签"
                visible={signModal}
                onOk={handleSign}
                onCancel={() => {
                    setSignModal(false);
                    setSignUser("");
                    setSignTime(undefined);
                }}
                className={style.popup}>
                <Row>
                    <Col span={12} className={style.popupLabel}>
                        学生姓名：
                    </Col>
                    <Col span={12} className={style.popupContent}>
                        {
                            _.find(data, item => item.studentNo === signUser)
                                ?.studentName
                        }
                    </Col>
                </Row>

                <Row>
                    <Col span={12} className={style.popupLabel}>
                        补签日期：
                    </Col>
                    <Col span={12} className={style.popupContent}>
                        {date && date.format("YYYY-MM-DD")}
                    </Col>
                </Row>

                <Row>
                    <Col span={12} className={style.popupLabel}>
                        <i className={style.required}>*</i> 补签时间：
                    </Col>
                    <Col span={12} className={style.popupContent}>
                        <TimePicker
                            onChange={time => setSignTime(time)}
                            value={signTime}
                        />
                    </Col>
                </Row>
            </Modal>
        </>
    );
}
